import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { TableProps } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import { Divider, Fx, Spin, Stack } from 'Components';
import { createSortDescription } from 'Components/Table/createColumns';
import { useStores } from 'Hooks/useStore';
import { getIsPending } from 'Stores/util';
import { DashboardContext } from 'Pages/Dashboard/DashboardContext';
import DashboardFilters from 'Pages/Dashboard/DashboardFilters/DashboardFilters';
import { DashboardFiltersValues } from 'Pages/Dashboard/Dashboard.types';
import { SystemRoleType } from 'Services/Api/Roles/Types';
import {
	getAbsDiffValue,
	getTrendDirection
} from 'Pages/Dashboard/Dashboard.helpers';
import {
	OverviewGroupSummary,
	OverviewScores,
	WidgetData
} from 'Services/Api/Dashboard/Types';
import {
	getProvidersForTenant,
	getComplianceSummary
} from 'Services/Api/PciCompliance/SAQDashboard.Service';
import StatsCard from './components/StatsCard/StatsCard';
import GroupSummaryTable from './GroupSummaryTable/GroupSummaryTable';
import Heading from './components/Heading';
import styles from './NewDashboard.module.scss';
import { getLeadPath } from 'Pages/App/AppLayout/AppHeader/AppHeader.helpers';

type ScoreKey = keyof Pick<
	OverviewScores,
	'phishingScore' | 'elearningScore' | 'saqCompliance' | 'scanning'
>;

const STATS_CARD_CONFIG: Array<{
	key: ScoreKey;
	title: string;
	tooltip: string;
}> = [
	{
		key: 'phishingScore',
		title: 'Phishing',
		tooltip:
			'Percentage of users passing simulated phishing attempts. Includes a one-month lookback to track progress.'
	},
	{
		key: 'elearningScore',
		title: 'Training',
		tooltip:
			'Completion percentage of assigned learning paths in your organisation. Includes a one-month lookback to track progress.'
	},
	{
		key: 'saqCompliance',
		title: 'SAQ Compliance',
		tooltip: 'SAQ Compliance Percentage represents the proportion of SAQs that are fully compliant out of the total SAQs assigned.'
	},
	{
		key: 'scanning',
		title: 'Scanning',
		tooltip: 'Scanning Compliance Percentage represents the proportion of scans that are fully compliant out of the total scans assigned.'
	}
];

function NewDashboard() {
	const stores = useStores();
	const { dashboardStore, authStore } = stores;
	const isSuperAdmin =
		authStore.currentUser?.primarySystemRole?.role ===
		SystemRoleType.SuperAdmin;

	const [filterValues, setFilterValues] = useState<DashboardFiltersValues>({});
	const [hasProperties, setHasProperties] = useState(true);
	const [saqScore, setSaqScore] = useState<number | null>(null);
	const [scanningScore, setScanningScore] = useState<number | null>(null);

	const { overview, status } = dashboardStore;
	const { pathname } = useLocation();

	const awstokenbracket = stores.authStore;
	const listallawstokenbracket = JSON.stringify(awstokenbracket);
	const newtokenlist = JSON.parse(listallawstokenbracket);
	let isuperadm = '';
	const checkrole = JSON.stringify(
		newtokenlist.currentUser.primarySystemRole.id
	);
	if (checkrole == '1') {
		isuperadm = '&awsUserIsSuperAdmin=true';
	}
	const leadPath = getLeadPath(pathname);
	localStorage.setItem('APIadmin', isuperadm);
	if (typeof leadPath === 'string') {
		localStorage.setItem('APIpath', leadPath);
	} else {
		localStorage.setItem('APIpath', '');
	}

	const adminGroupId = !isSuperAdmin
		? authStore.currentUser?.groups?.[0]?.id
		: undefined;
	const tenantId = filterValues.id ?? adminGroupId;

	useEffect(() => {
		dashboardStore.getOverviewScores(filterValues);
		dashboardStore.getOverviewGroupSummary(filterValues);
		// eslint-disable-next-line
	}, [filterValues]);

	useEffect(() => {
		if (!tenantId) {
			setSaqScore(null);
			setScanningScore(null);
			return;
		}

		const fetchScore = async (
			envKey: string,
			setter: (v: number | null) => void
		) => {
			try {
				const result = await getProvidersForTenant(String(tenantId));
				const providers = Array.isArray(result.data) ? result.data : [];
				const envName = (process.env[envKey] || '').trim().toLowerCase();
				const matched = providers.find(
					(x: { provider_id: number; provider_name: string }) =>
						x.provider_name.trim().toLowerCase() === envName
				);
				if (!matched) {
					setter(null);
					return;
				}
				const summary = await getComplianceSummary(
					Number(tenantId),
					matched.provider_id
				);
				setter(
					summary.compliant_percentage !== undefined
						? parseFloat(summary.compliant_percentage)
						: null
				);
			} catch {
				setter(null);
			}
		};

		fetchScore('REACT_APP_SAQ_COMPLIANCE_DASHBOARD', setSaqScore);
		fetchScore('REACT_APP_SCANNING_DASHBOARD', setScanningScore);
		// eslint-disable-next-line
	}, [tenantId]);

	useEffect(() => {
		return () => {
			stores.clearDashboardStore();
		};
		// eslint-disable-next-line
	}, []);

	const onDashboardFiltersChange = (value: DashboardFiltersValues) => {
		setFilterValues({
			...filterValues,
			...value
		});
	};

	const handleTableChange: TableProps<OverviewGroupSummary>['onChange'] = (
		_,
		__,
		sorter
	) => {
		const sort = createSortDescription<OverviewGroupSummary>(
			sorter as SorterResult<OverviewGroupSummary>
		);
		dashboardStore.getOverviewGroupSummary({
			...overview.groupSummary.params,
			sort
		});
	};

	const getCardData = (key: ScoreKey) => {
		if (key === 'saqCompliance') {
			return { value: saqScore, trend: null, trendDirection: null };
		}
		if (key === 'scanning') {
			return { value: scanningScore, trend: null, trendDirection: null };
		}
		const data = overview.scores?.[key] as WidgetData | undefined | null;
		return {
			value: data?.current ?? null,
			trend: getAbsDiffValue(data?.current ?? null, data?.hystorical),
			trendDirection: getTrendDirection(
				data?.current ?? null,
				data?.hystorical
			)
		};
	};

	const scoresLoading = getIsPending(status.getOverviewScores);

	return (
		<DashboardContext.Provider value={{ filters: filterValues, hasProperties }}>
			<Fx>
				<Fx column grow={1}>
					<Stack>
						<div className={styles.pageHeader}>
							<Fx
								align="middle"
								justify="space-between"
								className={styles.pageHeaderRow}
							>
								<Heading
									title="Dashboard"
									subHeading="Overview of metrics and performance."
									titleClassName={styles.pageTitle}
									subTitleClassName={styles.pageSubTitle}
								/>
								<DashboardFilters
									onChange={onDashboardFiltersChange}
									onSetHasProperties={setHasProperties}
								/>
							</Fx>
						</div>

						{scoresLoading ? (
							<div className={styles.spinWrapper}>
								<Spin />
							</div>
						) : (
							<Fx className={styles.statsRow} wrap>
								{STATS_CARD_CONFIG.map(({ key, title, tooltip }) => (
									<Fx
										key={key}
										grow={1}
										className={styles.statCardWrapper}
									>
										<StatsCard
											title={title}
											tooltip={tooltip}
											{...getCardData(key)}
										/>
									</Fx>
								))}
							</Fx>
						)}

						<GroupSummaryTable
							data={overview.groupSummary.data}
							loading={getIsPending(status.getOverviewGroupSummary)}
							onChange={handleTableChange}
						/>

						<Divider className={styles.divider} />
					</Stack>
				</Fx>
			</Fx>
		</DashboardContext.Provider>
	);
}

export default observer(NewDashboard);
